// Copper & Vine — Mock Data Layer
// All data is MOCKED for frontend-only implementation

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_df1c70a0-bf77-4e24-aebe-afbb97b210e1/artifacts/26mb6zrg_copper_vine_color.png";

export const caseStudies = [
  {
    id: 1,
    number: "01",
    slug: "scaling-without-chaos",
    title: "Scaling Without Scaling Chaos",
    icp: "Local Service Operator (Gym / Studio / Clinic)",
    situation: "The business was busy. Demand was strong. But cash flow was inconsistent.\n\nThe operation couldn't reliably reconcile what was booked, what was paid, and what was actually delivered. Exceptions were handled manually. The owner had become the override layer for policy, payments, and staffing decisions.\n\nGrowth was increasing fragility.",
    month3: {
      title: "Operational Integrity",
      items: [
        "Implemented Booked → Paid → Attended chain-of-custody",
        "Clarified override rights and enforcement",
        "Installed weekly ops review",
        "Reduced manual exceptions"
      ],
      summary: "The business became calmer and more predictable. Staffing decisions moved from intuition to data."
    },
    month6: {
      title: "Margin Stabilization",
      items: [
        "Revenue leakage meaningfully reduced",
        "Attendance compliance normalized",
        "Staffing aligned to verified utilization",
        "Owner stepped out of daily override loops"
      ],
      summary: "Profit variability decreased. Growth no longer required constant supervision."
    },
    month12: {
      title: "Governable Scale",
      items: [
        "Policies enforced without personality",
        "Capacity planning became reliable",
        "Hiring decisions tied to actual demand signals",
        "Owner focused on expansion instead of reconciliation"
      ],
      summary: ""
    },
    transformation: "The business shifted from reactive heroics to governed scale."
  },
  {
    id: 2,
    number: "02",
    slug: "from-activity-to-commercial-system",
    title: "From Activity to Commercial System",
    icp: "Startup Founder Post-Accelerator",
    situation: "The product had traction. The founder had visibility.\n\nBut the commercial story was unclear. Outreach was broad and founder-dependent. The team was building without a locked buyer narrative.\n\nMomentum lacked structure.",
    month3: {
      title: "Offer Clarity",
      items: [
        "Defined buyer profile and decision trigger",
        "Pressure-tested pricing logic",
        "Framed proof and outcomes clearly",
        'Installed \"no build without commercial reason\" discipline'
      ],
      summary: "Sales conversations became more coherent and consistent."
    },
    month6: {
      title: "Repeatable Motion",
      items: [
        "Outbound sequencing stabilized",
        "Founder stopped being the only closer",
        "Sales narrative adopted by team",
        "Focus gates prevented product drift"
      ],
      summary: "Activity began converting."
    },
    month12: {
      title: "Commercial Maturity",
      items: [
        "Clear revenue lanes",
        "Defined pipeline health metrics",
        "Scalable partnership logic",
        "Strategic introductions used with precision"
      ],
      summary: ""
    },
    transformation: "The company moved from potential to predictable commercial motion."
  },
  {
    id: 3,
    number: "03",
    slug: "brand-strength-operational-weakness",
    title: "Brand Strength, Operational Weakness",
    icp: "Consumer Product Creator",
    situation: "Strong social presence. High engagement. Frequent demand spikes.\n\nBut fulfillment strained capacity. Margin was unclear. IP was loosely protected. Attention was scaling faster than operations.",
    month3: {
      title: "Stabilization",
      items: [
        "Rebuilt pricing around margin reality",
        "Clarified deliverables and scope",
        "Standardized fulfillment paths",
        "Installed basic IP protection mechanisms"
      ],
      summary: "Launches became less chaotic."
    },
    month6: {
      title: "Predictable Delivery",
      items: [
        "Reduced fulfillment exceptions",
        "Lowered refund and rework patterns",
        "Cleaner forecasting tied to margin",
        "Demand converted without burnout"
      ],
      summary: "Operations began supporting growth instead of resisting it."
    },
    month12: {
      title: "Scalable Brand Infrastructure",
      items: [
        "Launch playbook formalized",
        "Repeatable revenue cycles",
        "Guardrails embedded into fulfillment",
        "Profit stability improved"
      ],
      summary: ""
    },
    transformation: "The brand stopped carrying the business. Infrastructure began carrying it."
  },
  {
    id: 4,
    number: "04",
    slug: "high-ambition-managed-risk",
    title: "High Ambition, Managed Risk",
    icp: "Executive Launching a Venture",
    situation: "The idea was strong. Capital was available.\n\nBut vendor proposals weren't pressure-tested. Hiring felt premature. Build sequencing lacked gates. Downside risk was high.",
    month3: {
      title: "Decision Governance",
      items: [
        "Defined milestone gates",
        "Sequenced infrastructure before experience",
        "Structured vendor accountability",
        "Clarified hiring triggers"
      ],
      summary: "Capital deployment became disciplined."
    },
    month6: {
      title: "Build Legibility",
      items: [
        "Roadmap tied to measurable outcomes",
        "Vendor performance governed",
        "Hiring aligned to validated stages",
        "Reduced premature spend"
      ],
      summary: "Confidence increased without overexposure."
    },
    month12: {
      title: "Defensible Momentum",
      items: [
        "Governed product roadmap",
        "Scalable hiring model",
        "Stronger investor conversations",
        "Reduced downside risk"
      ],
      summary: ""
    },
    transformation: "Momentum became defensible. Risk became managed instead of emotional."
  },
  {
    id: 5,
    number: "05",
    slug: "digitizing-access-without-removing-dignity",
    title: "Digitizing Access Without Removing Dignity",
    icp: "Public-Benefit Organization",
    situation: "Manual intake created friction and dignity loss. Staff carried heavy administrative burden. The system functioned but lacked humane design.",
    month3: {
      title: "Infrastructure Deployment",
      items: [
        "Digital intake implemented",
        "Workflow connected to operational controls",
        "Reduced manual handling"
      ],
      summary: "Friction decreased without sacrificing oversight."
    },
    month6: {
      title: "Operational Reallocation",
      items: [
        "Staff time shifted to service",
        "Reduced intake bottlenecks",
        "Improved visibility into demand patterns"
      ],
      summary: "The system stabilized."
    },
    month12: {
      title: "Sustainable Access",
      items: [
        "Improved throughput",
        "Consistent governance",
        "Dignity embedded in workflow",
        "Reduced administrative strain"
      ],
      summary: ""
    },
    transformation: "Infrastructure aligned with mission — sustainably."
  },
  {
    id: 6,
    number: "06",
    slug: "guardrails-before-growth",
    title: "Guardrails Before Growth",
    icp: "Care-Oriented Product Platform",
    situation: "Risk wasn't adoption. It was misuse, inconsistency, and operational overload after scale.\n\nMost teams build first and govern later.",
    month3: {
      title: "Governance Architecture",
      items: [
        "Designed workflows and escalation logic",
        "Embedded edge-case management",
        "Defined guardrails before user expansion"
      ],
      summary: "Onboarding didn't collapse operations."
    },
    month6: {
      title: "Operational Stability",
      items: [
        "Reduced support escalations",
        "Improved workflow consistency",
        "Clear policy enforcement inside product"
      ],
      summary: "The product behaved predictably."
    },
    month12: {
      title: "Scalable Trust",
      items: [
        "Abuse prevention matured",
        "Governance embedded into feature releases",
        "Support load stabilized relative to growth"
      ],
      summary: ""
    },
    transformation: "The platform became governable at scale — not just impressive at demo."
  }
];

export const faqItems = [
  {
    question: "Is this a mastermind?",
    blocks: [
      "No.",
      "The Advisory Circle is a structured advisory program — not a mastermind, not networking, and not a community-driven discussion group.",
      { label: "You receive:", list: ["A monthly private advisory session", "Systems-level correction", "Quarterly board-style reviews", "Optional anonymized group briefings"] },
      "This is a decision room, not a social experience."
    ]
  },
  {
    question: "Is camera required?",
    blocks: [
      "No.",
      "Camera is never required.",
      { label: "You may:", list: ["Join audio-only", "Join chat-only", "Attend live or watch recordings", "Participate privately"] },
      "Value does not require performance."
    ]
  },
  {
    question: "Do I have to share publicly to get value?",
    blocks: [
      "No.",
      "Private Pulse sessions are 1:1 and never recorded.",
      "Operator Lounge sessions are anonymized. No public hot seats. No required sharing.",
      "You can receive full value without presenting your business to the group."
    ]
  },
  {
    question: "What if I miss live sessions?",
    blocks: [
      "Live attendance is optional.",
      "Operator Lounge sessions are recorded and shared. Private Pulse sessions are scheduled directly by you within available blocks.",
      "This program is designed for working adults across time zones."
    ]
  },
  {
    question: "How often do I meet 1:1?",
    blocks: [
      "Each member is guaranteed one Private Pulse session per month (15\u201320 minutes).",
      "Additional sessions may be available based on capacity.",
      "Private sessions are concise, focused, and directive."
    ]
  },
  {
    question: "What happens in a Private Pulse?",
    blocks: [
      "We focus on one decision.",
      { label: "You leave with:", list: ["One clear next move", "One structural correction", "One thing to stop doing"] },
      "The session is short by design. Clarity does not require an hour."
    ]
  },
  {
    question: "What is the Operator Lounge?",
    blocks: [
      "Twice per month, we host a 60-minute strategic briefing.",
      { label: "It includes:", list: ["Anonymized patterns from member conversations", "A systems framework", "A practical implementation template"] },
      "You may attend live or watch the recording.",
      "There are no public hot seats."
    ]
  },
  {
    question: "What is the Quarterly Boardroom Review?",
    blocks: [
      { label: "Once per quarter, members submit:", list: ["Revenue range", "Primary constraint", "90-day strategic bet"] },
      "We review themes and provide structural correction and priority alignment.",
      "It functions like a board-level forcing mechanism without hiring a board."
    ]
  },
  {
    question: "Is this coaching?",
    blocks: [
      "No.",
      "Coaching centers on mindset and personal development.",
      { label: "The Advisory Circle centers on:", list: ["Revenue architecture", "Operational systems", "Decision governance", "Risk reduction"] },
      "This is operational advisory."
    ]
  },
  {
    question: "Is implementation included?",
    blocks: [
      "No.",
      { label: "The Advisory Circle provides:", list: ["Strategic direction", "System design guidance", "Decision correction"] },
      "If deeper implementation is required, that is structured separately (Strategy Day, Implementation Sprint, or Fractional engagement)."
    ]
  },
  {
    question: "Why is it a 12-month commitment?",
    blocks: [
      "Structural change requires rhythm.",
      "This is not a drop-in subscription. It is a governed advisory structure.",
      "We do offer a 30-day alignment window to ensure fit."
    ]
  },
  {
    question: "What is the 30-day alignment window?",
    blocks: [
      "After your first month of participation, if the program is not aligned, you may exit.",
      "This ensures confidence without creating instability in the room."
    ]
  },
  {
    question: "Who is this not for?",
    blocks: [
      { label: "This is not for:", list: ["Early-stage idea validation", "Those seeking motivation or community", "Those unwilling to share revenue ranges privately", "Those wanting unlimited access to advisory time", "Those looking for public visibility"] },
      "This is for operators ready to operate better."
    ]
  },
  {
    question: "Can my company pay for this?",
    blocks: [
      "Yes.",
      { label: "Many members expense the Advisory Circle as:", list: ["Executive development", "Strategic advisory", "Operational consulting"] },
      "We provide invoices and documentation as needed."
    ]
  },
  {
    question: "What is the Dinner Passport?",
    blocks: [
      { label: "An optional add-on that includes:", list: ["Priority RSVP to Executive Dinners globally", "One guaranteed dinner seat in your home city or regional hub annually", "Curated operator table with structured facilitation"] },
      "It is not required to receive value from the Advisory Circle."
    ]
  },
  {
    question: "How many members are in the Circle?",
    blocks: [
      "Membership is limited to preserve signal.",
      { label: "We maintain capacity that ensures:", list: ["Timely advisory", "Focused sessions", "High-quality participation"] },
      "This is not a high-volume program."
    ]
  },
  {
    question: "Is confidentiality protected?",
    blocks: [
      "Yes.",
      "Private sessions are never recorded.",
      "Operator Lounge discussions are anonymized.",
      "Chatham House Rule applies to all members.",
      "Confidentiality is foundational to the design."
    ]
  },
  {
    question: "How do I apply?",
    blocks: [
      "Submit the application form.",
      "If aligned, you\u2019ll be invited to a brief fit conversation.",
      "Membership is confirmed upon acceptance and payment."
    ]
  },
  {
    question: "What should I expect in the first 90 days?",
    blocks: [
      "Month 1: Decision stabilization",
      "Month 2: Systems tightening",
      "Month 3: Board-level clarity",
      { label: "You should expect:", list: ["Reduced noise", "Clearer priorities", "Stronger revenue discipline", "Calmer execution"] }
    ]
  }
];

export const revenueRanges = [
  "Under $500K",
  "$500K – $1M",
  "$1M – $3M",
  "$3M – $5M",
  "$5M – $10M",
  "$10M+"
];

export const tierOptions = [
  { value: "circle", label: "Advisory Circle — $18,000/year" },
  { value: "circle-passport", label: "Advisory Circle + Dinner Passport — $22,000/year" }
];

// MOCK: Authentication
export const mockAuth = {
  login: (email, password) => {
    if (email && password) {
      const user = { name: "Member", email, tier: "Advisory Circle + Dinner Passport" };
      localStorage.setItem('cv_auth_token', 'mock-jwt-token');
      localStorage.setItem('cv_user', JSON.stringify(user));
      return { success: true, user, token: 'mock-jwt-token' };
    }
    return { success: false, error: 'Invalid credentials' };
  },
  isAuthenticated: () => localStorage.getItem('cv_auth_token') !== null,
  getUser: () => {
    const user = localStorage.getItem('cv_user');
    return user ? JSON.parse(user) : null;
  },
  logout: () => {
    localStorage.removeItem('cv_auth_token');
    localStorage.removeItem('cv_user');
  }
};

// MOCK: Application submission
export const mockApplication = {
  submit: (formData) => {
    localStorage.setItem('cv_application', JSON.stringify({
      ...formData,
      submittedAt: new Date().toISOString(),
      status: 'pending'
    }));
    return { success: true, message: "Application received. We'll be in touch within 48 hours." };
  }
};
